<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  batchId: { type: [String, Number], required: true },
});

const emit = defineEmits(['close', 'success']);
const store = useStore();
const { t } = useI18n();

const uiFlags = useMapGetter('campaignBatches/getUIFlags');
const isImporting = computed(() => uiFlags.value.isImporting);

const file = ref(null);
const nameColumn = ref('name');
const phoneColumn = ref('phone_number');
const isDragOver = ref(false);

const fileName = computed(() => file.value?.name || '');

const handleFileSelect = event => {
  const selected = event.target.files?.[0];
  if (selected) file.value = selected;
};

const handleDrop = event => {
  isDragOver.value = false;
  const dropped = event.dataTransfer?.files?.[0];
  if (dropped && (dropped.name.endsWith('.csv') || dropped.type === 'text/csv')) {
    file.value = dropped;
  }
};

const handleImport = async () => {
  if (!file.value) return;

  try {
    const result = await store.dispatch('campaignBatches/importCsv', {
      batchId: props.batchId,
      file: file.value,
      nameColumn: nameColumn.value,
      phoneColumn: phoneColumn.value,
    });
    useAlert(
      t('CAMPAIGN.BATCH.CSV_IMPORT.SUCCESS', {
        added: result.added_count,
        skipped: result.skipped_count,
      })
    );
    emit('success', result);
  } catch (e) {
    useAlert(t('CAMPAIGN.BATCH.CSV_IMPORT.ERROR'));
  }
};
</script>

<template>
  <div
    class="fixed inset-0 bg-n-alpha-black2 z-50 flex items-center justify-center p-4"
    @click.self="emit('close')"
  >
    <div
      class="w-full max-w-lg bg-n-solid-2 backdrop-blur-[100px] rounded-xl border border-n-weak shadow-lg max-h-[85vh] overflow-y-auto"
    >
      <div class="p-6 flex flex-col gap-5">
        <div class="flex items-center justify-between">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('CAMPAIGN.BATCH.CSV_IMPORT.TITLE') }}
          </h3>
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            icon="i-lucide-x"
            @click="emit('close')"
          />
        </div>

        <p class="text-sm text-n-slate-11">
          {{ t('CAMPAIGN.BATCH.CSV_IMPORT.DESCRIPTION') }}
        </p>

        <div
          class="border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer"
          :class="
            isDragOver
              ? 'border-n-blue-8 bg-n-blue-2'
              : 'border-n-weak hover:border-n-slate-8'
          "
          @dragover.prevent="isDragOver = true"
          @dragleave="isDragOver = false"
          @drop.prevent="handleDrop"
          @click="$refs.fileInput.click()"
        >
          <input
            ref="fileInput"
            type="file"
            accept=".csv,text/csv"
            class="hidden"
            @change="handleFileSelect"
          />
          <div class="flex flex-col items-center gap-2">
            <i class="i-lucide-upload-cloud text-3xl text-n-slate-10" />
            <p v-if="fileName" class="text-sm font-medium text-n-slate-12">
              {{ fileName }}
            </p>
            <p v-else class="text-sm text-n-slate-11">
              {{ t('CAMPAIGN.BATCH.CSV_IMPORT.DROP_ZONE') }}
            </p>
            <p class="text-xs text-n-slate-10">
              {{ t('CAMPAIGN.BATCH.CSV_IMPORT.FORMAT_HINT') }}
            </p>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-3">
          <Input
            v-model="nameColumn"
            :label="t('CAMPAIGN.BATCH.CSV_IMPORT.NAME_COLUMN')"
            placeholder="name"
          />
          <Input
            v-model="phoneColumn"
            :label="t('CAMPAIGN.BATCH.CSV_IMPORT.PHONE_COLUMN')"
            placeholder="phone_number"
          />
        </div>

        <div class="flex gap-3 justify-between items-center w-full pt-2">
          <Button
            variant="faded"
            color="slate"
            type="button"
            :label="t('CAMPAIGN.BATCH.CSV_IMPORT.CANCEL')"
            class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
            @click="emit('close')"
          />
          <Button
            :label="t('CAMPAIGN.BATCH.CSV_IMPORT.IMPORT')"
            class="w-full"
            icon="i-lucide-file-spreadsheet"
            :is-loading="isImporting"
            :disabled="isImporting || !file"
            @click="handleImport"
          />
        </div>
      </div>
    </div>
  </div>
</template>
